import { useRef, useState, useEffect } from 'react';
import { getPointerCoords } from '../utils/whiteboardUtils';

export const STICKER_TYPES = [
  { key: 'sticky', color: '#fef08a', textColor: '#854d0e', border: '#facc15' },
  { key: 'callout', color: '#dbeafe', textColor: '#1e40af', border: '#3b82f6' },
  { key: 'card', color: '#fecaca', textColor: '#991b1b', border: '#ef4444' },
  { key: 'tag', color: '#dcfce7', textColor: '#166534', border: '#22c55e' },
  { key: 'note', color: '#f3e8ff', textColor: '#6b21a8', border: '#a855f7' },
];

export const useWhiteboardSticker = (setTool, setSelectedShape, setShapes, panRef, svgRef, tool) => {
  const [stickerPopup, setStickerPopup] = useState(false);
  const [editingStickerId, setEditingStickerId] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const stickerEditRef = useRef(null);
  const textEditRef = useRef(null);
  const [draggedSticker, setDraggedSticker] = useState(null);

  const handleStickerClick = () => {
    setTool('sticker');
    setStickerPopup(v => !v);
  };

  // Click outside sticker deselects — only when clicking inside the whiteboard SVG
  useEffect(() => {
    const handleClick = e => {
      if (!svgRef?.current?.contains(e.target)) return;
      if (!e.target.closest('.sticker-foreign')) {
        if (!window.isResizing) {
          setSelectedShape(null);
          setEditingStickerId(null);
        }
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Autofocus on sticker edit
  useEffect(() => {
    if (editingStickerId && stickerEditRef.current) {
      stickerEditRef.current.focus();
    }
  }, [editingStickerId]);

  // Autofocus on text edit
  useEffect(() => {
    if (editingTextId && textEditRef.current) {
      const timeoutId = setTimeout(() => {
        if (
          textEditRef.current &&
          textEditRef.current.parentNode &&
          editingTextId &&
          document.body.contains(textEditRef.current) &&
          textEditRef.current.offsetParent !== null
        ) {
          try {
            textEditRef.current.focus();
          } catch (error) {
            console.warn('Ошибка при установке фокуса:', error);
          }
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [editingTextId]);

  // eslint-disable-next-line no-unused-vars
  const handleStickerMouseDown = (e, s) => {
    e.stopPropagation();
    if (editingStickerId === s.id) return;
    if (tool !== 'select') return;
    setDraggedSticker(s.id);
  };

  useEffect(() => {
    if (!draggedSticker) return;
    const handleMove = e => {
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
      setShapes(prev => prev.map(sh =>
        sh.id === draggedSticker ? { ...sh, x, y } : sh
      ));
    };
    const handleUp = () => setDraggedSticker(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggedSticker]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleStickerResizeMouseDown = (e, corner, s) => {
    e.stopPropagation();
    e.preventDefault();

    if (!Array.isArray(corner) || corner.length !== 2) {
      console.warn('Invalid corner parameter:', corner);
      return;
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = s.w;
    const startH = s.h;
    const startX0 = s.x;
    const startY0 = s.y;
    const minSize = 60;

    function onMove(ev) {
      let dx = ev.clientX - startX;
      let dy = ev.clientY - startY;
      let newW = startW;
      let newH = startH;
      let newX = startX0;
      let newY = startY0;
      if (corner[0] === 1) newW = Math.max(minSize, startW + dx);
      if (corner[0] === 0) { newW = Math.max(minSize, startW - dx); newX = startX0 + dx; }
      if (corner[1] === 1) newH = Math.max(minSize, startH + dy);
      if (corner[1] === 0) { newH = Math.max(minSize, startH - dy); newY = startY0 + dy; }
      setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, w: newW, h: newH, x: newX, y: newY } : sh));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return {
    stickerPopup, setStickerPopup,
    editingStickerId, setEditingStickerId,
    editingTextId, setEditingTextId,
    stickerEditRef, textEditRef,
    draggedSticker, setDraggedSticker,
    handleStickerClick, handleStickerMouseDown, handleStickerResizeMouseDown,
  };
};
